// User-visible message strings — toasts, validation errors, and inline error
// states. Organised by the page or component that raises them.
// Dynamic messages (those needing runtime values) are exported as functions.

// ── Common messages ───────────────────────────────────────────────────────────
// Generic, caller-agnostic messages. Check here before defining a new constant
// — if the wording would be identical or near-identical, reuse from this section.

export const GENERIC_ERROR_MESSAGE =
  'An error occurred. Please try again later.';
export const DATA_BACKEND_ERROR_MESSAGE =
  'Error fetching data. There may be an issue with the database or backend services.';
export const DATA_FETCH_ERROR_MESSAGE = 'Error while fetching data';
export const VALUE_REQUIRED_MESSAGE = 'Required';
export const VALUE_AT_LEAST_MESSAGE = (min: number) =>
  `Must be ${min} or greater`;
export const VALUE_GREATER_THAN_MESSAGE = (min: number) =>
  `Must be greater than ${min}`;

// ── HTTP interceptor (AxiosInstance) ─────────────────────────────────────────

export const LICENCE_EXPIRED_API_ERROR_MESSAGE =
  'Your licence has expired or is inactive. Please contact support.';
export const INVALID_REQUEST_API_ERROR_MESSAGE =
  'Invalid request. Please check your data and try again.';
export const INVALID_REQUEST_RESOURCE_API_ERROR_MESSAGE = (resource: string) =>
  `Invalid ${resource} request. Please check your data and try again.`;
export const FORBIDDEN_API_ERROR_MESSAGE =
  'You do not have permission to perform this action.';
export const FORBIDDEN_RESOURCE_API_ERROR_MESSAGE = (resource: string) =>
  `You do not have permission to access this ${resource}.`;
export const CONFLICT_API_ERROR_MESSAGE =
  'A conflict occurred. The record may have been modified elsewhere.';
export const CONFLICT_RESOURCE_API_ERROR_MESSAGE = (resource: string) =>
  `A conflict occurred saving this ${resource}. It may have been modified elsewhere.`;
export const NOT_FOUND_API_ERROR_MESSAGE =
  'The requested resource could not be found.';
export const NOT_FOUND_RESOURCE_API_ERROR_MESSAGE = (resource: string) =>
  `This ${resource} could not be found.`;

// ── Workspace initial load (FarmsPage) ───────────────────────────────────────

export const WORKSPACE_LOAD_FATAL_ERROR_MESSAGE =
  'Failed to load workspace data. Please refresh the page or log in again.';
export const FARMS_LOAD_ERROR_MESSAGE =
  'Farm data failed to load. Some features may be unavailable.';
export const ZONES_LOAD_ERROR_MESSAGE =
  'Zone data failed to load. Some features may be unavailable.';
export const INPUTS_LOAD_ERROR_MESSAGE =
  'Input data failed to load. Some features may be unavailable.';
export const UNITS_LOAD_ERROR_MESSAGE =
  'Unit data failed to load. Some features may be unavailable.';

// ── Profile page ──────────────────────────────────────────────────────────────

export const PROFILE_LOAD_ERROR_MESSAGE =
  'Failed to load profile data. Please try again.';
export const PROFILE_SAVE_SUCCESS_MESSAGE = 'Profile saved successfully.';
export const PROFILE_SAVE_ERROR_MESSAGE =
  'Failed to update profile. Please try again.';

export const PROFILE_EMAIL_REQUIRED_MESSAGE = 'Email is required';
export const PROFILE_FIRST_NAME_REQUIRED_MESSAGE = 'First name is required';
export const PROFILE_FIRST_NAME_PATTERN_MESSAGE =
  'First name can only contain letters, spaces, and hyphens';
export const PROFILE_LAST_NAME_REQUIRED_MESSAGE = 'Last name is required';
export const PROFILE_LAST_NAME_PATTERN_MESSAGE =
  'Last name can only contain letters, spaces, and hyphens';
export const PROFILE_PREFERRED_NAME_PATTERN_MESSAGE =
  'Preferred name can only contain letters, spaces, and hyphens';
export const PROFILE_MOBILE_PHONE_REQUIRED_MESSAGE =
  'Mobile phone is required';
export const PROFILE_MOBILE_PHONE_PATTERN_MESSAGE =
  'Please enter a valid Australian mobile number (e.g., +61412345678 or 0412345678)';
export const PROFILE_ALTERNATE_PHONE_PATTERN_MESSAGE =
  'Please enter a valid phone number.';
export const PROFILE_ADDRESS1_REQUIRED_MESSAGE = 'Address line 1 is required';
export const PROFILE_CITY_REQUIRED_MESSAGE = 'City is required';
export const PROFILE_STATE_REQUIRED_MESSAGE = 'State is required';
export const PROFILE_POSTCODE_REQUIRED_MESSAGE = 'Postcode is required';
export const PROFILE_POSTCODE_PATTERN_MESSAGE =
  'Please enter a valid 4-digit Australian postcode';
export const PROFILE_ROLE_REQUIRED_MESSAGE = 'Role is required';
export const PROFILE_SEGMENTS_REQUIRED_MESSAGE =
  'At least one segment is required';
export const PROFILE_SEGMENTS_MIN_MESSAGE =
  'Please select at least one segment';

// ── Global Catalogue — TableGrid / GridRightPanel ─────────────────────────────

export const GC_COLLECTION_UPDATED_SUCCESS_MESSAGE =
  'Collection updated successfully';
export const GC_COLLECTION_DISABLED_SUCCESS_MESSAGE =
  'Collection disabled successfully';
export const GC_DOCUMENT_CREATED_SUCCESS_MESSAGE =
  'Document created successfully';
export const GC_DOCUMENT_UPDATE_GUARD_ERROR_MESSAGE =
  'Error updating document. Please try again.';

// ── Global Catalogue — organisation mutation ──────────────────────────────────

export const GC_ORG_BACKEND_ERROR_MESSAGE =
  'Error fetching organisation data. There may be an issue with the database or backend services.';
export const GC_ORG_FETCH_ERROR_MESSAGE =
  'Error while fetching organisation data';

// ── Global Catalogue — reference collection mutation hooks ────────────────────

export const GC_ACTION_MODE_FETCH_ERROR_MESSAGE =
  'Error while fetching Action Mode collection data';
export const GC_AG_SECTOR_FETCH_ERROR_MESSAGE =
  'Error while fetching AgSector collection data';
export const GC_CHEMICAL_GROUP_FETCH_ERROR_MESSAGE =
  'Error while fetching ChemicalGroup collection data';
export const GC_GENUS_SPECIES_FETCH_ERROR_MESSAGE =
  'Error while fetching GenusSpecies Collection data';
export const GC_MAIN_END_USE_FETCH_ERROR_MESSAGE =
  'Error while fetching Main End Use collection data';
export const GC_MANUFACTURER_FETCH_ERROR_MESSAGE =
  'Error while fetching Manufacturer collection data';
export const GC_MOA_GROUP_FETCH_ERROR_MESSAGE =
  'Error while fetching Moa Group collection data';
export const GC_PARAMETER_FETCH_ERROR_MESSAGE =
  'Error while fetching Parameter collection data';
export const GC_PHYSICAL_STATE_FETCH_ERROR_MESSAGE =
  'Error while fetching Physical State collection data';
export const GC_PRODUCT_GRADE_FETCH_ERROR_MESSAGE =
  'Error while fetching Product Grade collection data';
export const GC_PRODUCT_GROUP_FETCH_ERROR_MESSAGE =
  'Error while fetching Product Group collection data';
export const GC_SEGMENT_FETCH_ERROR_MESSAGE =
  'Error while fetching Segment collection data';
export const GC_SPECIES_TYPE_FETCH_ERROR_MESSAGE =
  'Error while fetching Species Type collection data';
export const GC_TECH_STACK_FETCH_ERROR_MESSAGE =
  'Error while fetching Tech Stack collection data';

// ── Global Catalogue — unit / lookup mutation hooks ───────────────────────────

export const GC_SUPPLIER_NAMES_FETCH_ERROR_MESSAGE =
  'Error while fetching Active Supplier Names';
export const GC_SEED_SPECIES_FETCH_ERROR_MESSAGE =
  'Error while fetching Seed Species Crops data';

// ── Global Catalogue — collection search / read ───────────────────────────────

export const GC_SEARCH_FAILED_MESSAGE = (err: string): string =>
  `Search failed: ${err}`;
export const GC_FETCH_TOTAL_PAGES_FAILED_MESSAGE = (error: unknown): string =>
  `Failed to fetch total pages: ${error}`;

// ── Map canvas (PaddockMapCanvas) ─────────────────────────────────────────────

export const MAP_HOLE_POINT_OUTSIDE_BOUNDARY_MESSAGE =
  'Points must be inside the paddock boundary.';
export const MAP_HOLE_SEGMENT_CROSSES_HOLE_MESSAGE =
  'Line segment must not cross an existing hole.';
export const MAP_SPLIT_CUT_INVALID_MESSAGE =
  'Cut line must cross the paddock from one side to the other.';
export const MAP_BOUNDARY_SELF_INTERSECTS_MESSAGE =
  'Invalid boundary — the polygon edges cross each other. Please redraw.';
export const MAP_BOUNDARY_MIN_POINTS_MESSAGE =
  'A boundary requires at least 3 points.';
export const MAP_VERTEX_DELETE_MIN_VERTICES_MESSAGE =
  'Cannot delete — ring must retain at least 3 vertices.';
export const MAP_HOLE_OUTSIDE_BOUNDARY_MESSAGE = (i: number): string =>
  `Hole ${i + 1} extends outside the paddock boundary.`;
export const MAP_HOLES_OVERLAP_MESSAGE = (i: number, j: number): string =>
  `Hole ${i + 1} and hole ${j + 1} overlap — holes must not intersect.`;
export const MAP_SPLIT_NO_VALID_CUTS_MESSAGE =
  'Could not split: no valid cut lines.';
export const MAP_CUTOUT_MIN_POINTS_MESSAGE =
  'A cutout requires at least 3 points.';
export const MAP_CUTOUT_SELF_INTERSECTS_MESSAGE =
  'Invalid cutout — the polygon edges cross each other. Please redraw.';
export const MAP_CUTOUT_OUTSIDE_BOUNDARY_MESSAGE =
  'Cutout must be entirely within the paddock boundary.';

// ── Tasks workspace ───────────────────────────────────────────────────────────

export const TARGET_SPECIES_DUPLICATES_SKIPPED_MESSAGE = (
  count: number
): string =>
  `${count} duplicate ${count === 1 ? 'target' : 'targets'} already added – skipped.`;
export const TASK_SAVE_ERROR_MESSAGE = 'Error saving task. Please try again.';
export const TASK_DELETE_ERROR_MESSAGE =
  'Error deleting task. Please try again.';
// Short enough to sit beside the Task column label (OP-2534).
export const EMPTY_TASK_REMOVED_MESSAGE = 'Empty task removed';
export const INPUT_DOCUMENT_UPDATE_ERROR_MESSAGE =
  'Error updating document. Please try again.';

// ── Create plan modal ─────────────────────────────────────────────────────────

export const CREATE_PLAN_ERROR_MESSAGE =
  'Unable to create paddock plan. Please try again.';

// ── Network status ────────────────────────────────────────────────────────────

export const NETWORK_OFFLINE_BANNER_MESSAGE =
  'No network connection detected. Please check your connection.';

// ── Reset password page ───────────────────────────────────────────────────────

export const RESET_PASSWORD_SUCCESS_MESSAGE = 'Your password has been reset.';

// ── Plan preview — Insights and Farm diagnostics sections (OP-2490) ─────────
// On screen the adviser is told how to fill an empty section; the printed
// copy goes to the grower, so it carries a neutral line instead.
export const PLAN_PREVIEW_DIAGNOSTICS_GHOST_MESSAGE =
  'Not enough data yet — add rainfall and yield in Farm diagnostics → Yearly overview, then reopen this preview.';
export const PLAN_PREVIEW_PRINT_NO_HISTORY_MESSAGE = 'Not enough history';
export const PLAN_PREVIEW_ROTATION_GHOST_MESSAGE =
  'Not enough data yet — add rainfall and management practice in Farm diagnostics → Rotation efficiency, then reopen this preview.';
export const PLAN_PREVIEW_DIAGNOSTICS_NO_PADDOCK_MESSAGE =
  'This plan is not linked to a paddock, so it has no paddock history.';
export const PLAN_PREVIEW_NO_NUTRIENTS_MESSAGE =
  'No nutrients planned — add nutrient values to this plan’s soil inputs to see a tally.';
export const PLAN_PREVIEW_NO_NUTRIENTS_APPLIED_MESSAGE =
  'No nutrients applied yet — record soil inputs with nutrient values in this plan’s actual tasks to see a tally.';
export const PLAN_PREVIEW_NO_ACTUALS_MESSAGE =
  'No actuals recorded for this plan yet — record completed tasks to see them here.';
export const PLAN_PREVIEW_NO_SECTIONS_MESSAGE =
  'Every paddock section is turned off — use What to include to show them.';

// ── Insights — Nutrition lens (OP-2508) ──────────────────────────────────────

export const NUTRITION_INVALID_SCOPE_MESSAGE =
  'Select your organisation, a farm or a paddock in the farm tree to see the nutrient audit.';
export const NUTRITION_NO_PLANS_MESSAGE =
  'No plans in these years — create a plan for this paddock, or choose more years.';
export const NUTRITION_NO_PADDOCK_DATA_MESSAGE =
  'No nutrient data yet — add fertilisers with a nutrient analysis, and a harvest yield, to see the balance.';
export const NUTRITION_CHART_NO_DATA_MESSAGE =
  'Add fertilisers with a nutrient analysis and a harvest yield to fill this chart.';
export const NUTRITION_NO_NUTRIENTS_SELECTED_MESSAGE =
  'Choose at least one nutrient to show.';
// Nutrition tooltips are a short heading plus short lines (TipContent), so
// they read at a glance rather than as one long sentence.
export const NUTRITION_MISSING_REMOVAL_MESSAGE = (seasons: number[]) => ({
  title: `Crop removal not counted for ${seasons.join(', ')}`,
  lines: [
    'There’s no harvest yield, or the crop has no grain nutrient content.',
    'Add a yield to the plan’s Production task to include it.',
  ],
});
export const NUTRITION_NO_REMOVAL_KEY_MESSAGE =
  'There’s no crop removal figure for this nutrient, so only the amount applied is shown.';
export const NUTRITION_BALANCE_HELP = {
  title: 'How the balance is worked out',
  lines: [
    'Applied: each fertiliser’s nutrient analysis × its rate.',
    'Removed: harvest yield × the grain’s nutrient content. In Plan, it uses the yield chosen under Yield. Stubble isn’t counted.',
    'Balance: applied − removed, over the years shown.',
    '▲ Building above zero. ▼ Declining below zero.',
  ],
};
export const NUTRITION_RATE_LOCKED_OPEN_PLAN_MESSAGE = {
  title: 'Open in Planning',
  lines: [
    'Change this rate in Planning, or select the farm in the tree to change it here.',
  ],
};
export const NUTRITION_RATE_LOCKED_UNSAVED_MESSAGE = {
  title: 'Unsaved changes in Planning',
  lines: ['Save or discard them there first.'],
};
export const NUTRITION_RATE_LOCKED_AMBIGUOUS_STEPS_MESSAGE = {
  title: 'Duplicate entries',
  lines: ['This application has duplicate entries. Change its rates in Planning.'],
};
export const NUTRITION_RATE_EDIT_HINT =
  'Click a rate to change it. The change is saved to that plan straight away.';
export const NUTRITION_SEASON_NO_PLAN_MESSAGE =
  'No plan this year. Create one from the farm tree to set its nutrition program.';
export const NUTRITION_OVER_AREA_MESSAGE = (applied: string, plan: string) => ({
  title: `${applied} ha is more than the plan’s ${plan} ha`,
  lines: [
    'Its nutrients are overstated.',
    'Check the product’s area, or the plan’s, in Planning.',
  ],
});
export const NUTRITION_NOT_SAVED_MESSAGE = (
  failed: { description: string; reason: string }[]
) =>
  `${failed.length === 1 ? '1 change' : `${failed.length} changes`} not saved: ${failed
    .map((f) => `${f.description} (${f.reason})`)
    .join('; ')}. Click Retry to try again.`;
export const NUTRITION_FARM_TOTAL_HELP = {
  title: 'Area-weighted total',
  lines: ['Each paddock’s balance weighted by its area, so larger paddocks count for more.'],
};
